import { API_METHODS } from '@compass/client/constants/api-methods';
import { type ManualTask, ManualTaskStatus } from '@compass/client/types/manual_task';
import { useCanCallAPI } from '@hooks/usePermissions';
import { useUsersByIds } from '@hooks/useUsers';
import { useStore } from '@store/index';
import { formatDistanceToNow } from 'date-fns';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { CollapsibleSidebar } from '@/components/CollapsibleSidebar';
import { FilterChip } from '@/components/FilterChip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { UserAvatar } from '@/components/UserAvatar';
import { useCaseTasks, useUpdateManualTaskStatus } from '@/hooks/useCaseTasks';
import { cn } from '@/utils';
import {
  isPendingTask,
  taskStatusBadgeVariant,
  taskStatusLabel,
  taskTypeLabel,
} from '@/utils/manualTasks';

type TaskFilter = 'all' | 'pending' | 'completed' | 'mine';

const FILTERS: { key: TaskFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'completed', label: 'Completed' },
  { key: 'mine', label: 'Assigned to me' },
];

const ago = (ts: string | undefined) =>
  ts ? formatDistanceToNow(new Date(ts), { addSuffix: true }) : undefined;

/** Two-line clamp with a Show more toggle that only appears when the text overflows. */
const ClampedText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight);
  }, [text, expanded]);

  return (
    <div>
      <p ref={ref} className={cn('whitespace-pre-wrap', !expanded && 'line-clamp-2', className)}>
        {text}
      </p>
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const StartTaskButton: React.FC<{ task: ManualTask; caseRecordId: string }> = ({
  task,
  caseRecordId,
}) => {
  const { mutate, isPending } = useUpdateManualTaskStatus(caseRecordId);
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 shrink-0"
      disabled={isPending}
      onClick={() => mutate({ task, status: ManualTaskStatus.MANUAL_TASK_STATUS_STARTED })}
    >
      {isPending ? 'Starting…' : 'Start task'}
    </Button>
  );
};

const CompleteTaskForm: React.FC<{ task: ManualTask; caseRecordId: string }> = ({
  task,
  caseRecordId,
}) => {
  const [notes, setNotes] = useState('');
  const { mutate, isPending } = useUpdateManualTaskStatus(caseRecordId);

  return (
    <div className="space-y-2 pt-1">
      <Textarea
        aria-label="Notes"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="What you did, in your own words…"
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={isPending}
          onClick={() =>
            mutate({
              task,
              status: ManualTaskStatus.MANUAL_TASK_STATUS_COMPLETED,
              notes: notes.trim(),
            })
          }
        >
          {isPending ? 'Completing…' : 'Complete task'}
        </Button>
      </div>
    </div>
  );
};

/** "created 2 hours ago", "completed by Meghan W. 3 days ago", … for the card's meta row. */
const timeLabel = (task: ManualTask, nameOf: (id: string | undefined) => string) => {
  switch (task.status) {
    case ManualTaskStatus.MANUAL_TASK_STATUS_COMPLETED: {
      // Only name the completer when it is not the assignee already shown on the row.
      const by =
        task.completed_by_id && task.completed_by_id !== task.user_id
          ? ` by ${nameOf(task.completed_by_id)}`
          : '';
      return `completed${by} ${ago(task.completed_at) ?? ''}`.trim();
    }
    case ManualTaskStatus.MANUAL_TASK_STATUS_STARTED:
      return `started ${ago(task.started_at) ?? ''}`.trim();
    case ManualTaskStatus.MANUAL_TASK_STATUS_CANCELLED:
      return `cancelled ${ago(task.updated_at) ?? ''}`.trim();
    default:
      return `created ${ago(task.created_at) ?? ''}`.trim();
  }
};

const TaskCard: React.FC<{
  task: ManualTask;
  nameOf: (id: string | undefined) => string;
  canUpdate: boolean;
  caseRecordId: string;
}> = ({ task, nameOf, canUpdate, caseRecordId }) => {
  const open = isPendingTask(task.status);
  const notes = task.output?.notes;
  const assignee = nameOf(task.user_id);
  // The model allows pending -> started -> completed only, so each open state gets one action.
  const canStart = canUpdate && task.status === ManualTaskStatus.MANUAL_TASK_STATUS_PENDING;
  const canComplete = canUpdate && task.status === ManualTaskStatus.MANUAL_TASK_STATUS_STARTED;

  return (
    <div className="rounded-lg border bg-card p-3 space-y-2 text-sm">
      <div className="flex items-start justify-between gap-2">
        <span className={cn('font-semibold', !open && 'text-muted-foreground')}>
          {taskTypeLabel(task.type)}
        </span>
        <Badge variant={taskStatusBadgeVariant(task.status)} className="shrink-0">
          {taskStatusLabel(task.status)}
        </Badge>
      </div>

      {task.description && (
        <ClampedText
          text={task.description}
          className={open ? 'text-foreground' : 'text-muted-foreground'}
        />
      )}

      {notes && !canComplete && (
        <ClampedText text={notes} className="border-l-2 pl-2 text-muted-foreground italic" />
      )}

      <div className="flex items-center gap-2">
        <UserAvatar name={assignee} userId={task.user_id} size="xs" className="shrink-0" />
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {assignee} · {timeLabel(task, nameOf)}
        </span>
        {canStart && <StartTaskButton task={task} caseRecordId={caseRecordId} />}
      </div>

      {canComplete && <CompleteTaskForm task={task} caseRecordId={caseRecordId} />}
    </div>
  );
};

interface TasksColumnProps {
  caseRecordId: string;
  isCollapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

/**
 * Right-side collapsible column listing every manual task on the case, oldest first.
 * Opens on the Pending filter. Users allowed to update tasks start a pending task from its
 * card, then complete it with notes once started.
 */
export const TasksColumn: React.FC<TasksColumnProps> = ({
  caseRecordId,
  isCollapsed,
  onCollapsedChange,
}) => {
  const [filter, setFilter] = useState<TaskFilter>('pending');
  const currentUserId = useStore(state => state.user?.id);
  // UpdateManualTask needs manual_task:update:program (Case Manager, Program Admin/Manager;
  // all:all for admins); hide the actions from anyone who would get a 403.
  const canUpdate = useCanCallAPI(API_METHODS.ManualTaskService.UpdateManualTask);
  const { tasks, isLoading, error } = useCaseTasks(caseRecordId, !isCollapsed);

  const userIds = useMemo(
    () => tasks.flatMap(t => [t.user_id, t.completed_by_id]).filter((id): id is string => !!id),
    [tasks],
  );
  const { usersMap } = useUsersByIds(userIds);
  const nameOf = (userId: string | undefined) => {
    // An empty user_id is an unassigned task (e.g. raised by a workflow), not a missing user.
    if (!userId) return 'Unassigned';
    if (userId === currentUserId) return 'You';
    const user = usersMap.get(userId);
    return user?.name || user?.email || 'Unknown';
  };

  const matches = useMemo<Record<TaskFilter, (t: ManualTask) => boolean>>(
    () => ({
      all: () => true,
      pending: t => isPendingTask(t.status),
      completed: t => t.status === ManualTaskStatus.MANUAL_TASK_STATUS_COMPLETED,
      mine: t => !!currentUserId && t.user_id === currentUserId,
    }),
    [currentUserId],
  );
  const visibleTasks = tasks.filter(matches[filter]);

  return (
    <CollapsibleSidebar
      label="Tasks"
      isCollapsed={isCollapsed}
      onCollapsedChange={onCollapsedChange}
      // Wider than Notes/Timeline (360px): cards carry a description and a notes box.
      expandedWidth="w-[440px]"
    >
      <div className="px-4 py-3 flex flex-wrap gap-1.5">
        {FILTERS.map(({ key, label }) => (
          <FilterChip
            key={key}
            label={label}
            count={tasks.filter(matches[key]).length}
            active={filter === key}
            onClick={() => setFilter(key)}
          />
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-3">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <p className="text-center py-8 text-sm text-destructive">Failed to load tasks</p>
        ) : visibleTasks.length === 0 ? (
          <p className="text-center py-8 text-sm text-muted-foreground">No tasks found</p>
        ) : (
          <div className="space-y-2">
            {visibleTasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                nameOf={nameOf}
                // Only the assignee works a task. UI-only: UpdateManualTask itself accepts any
                // task in the program from a caller with manual_task:update:program.
                canUpdate={canUpdate && !!currentUserId && task.user_id === currentUserId}
                caseRecordId={caseRecordId}
              />
            ))}
          </div>
        )}
      </div>
    </CollapsibleSidebar>
  );
};
